<template>
  <div class="p-4 md:p-6 max-w-5xl">
    <!-- Back -->
    <button
      class="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-5"
      @click="$router.back()"
    >
      <span class="material-icons text-sm">arrow_back</span>
      Torna alla lista
    </button>

    <!-- Skeleton -->
    <div v-if="loading" class="space-y-4">
      <div class="h-24 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"></div>
      <div class="grid grid-cols-2 gap-4">
        <div class="h-40 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"></div>
        <div class="h-40 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"></div>
      </div>
    </div>

    <!-- Not found -->
    <div
      v-else-if="!detail"
      class="bg-white dark:bg-gray-900 rounded-xl shadow p-12 text-center text-gray-400"
    >
      <span class="material-icons text-5xl mb-3 block">person_off</span>
      <p class="text-lg font-medium">Utente non trovato.</p>
    </div>

    <template v-else>
      <!-- ── HEADER CARD ───────────────────────────────────── -->
      <div class="bg-white dark:bg-gray-900 rounded-xl shadow p-5 mb-4 flex flex-wrap items-start gap-4">
        <!-- Avatar placeholder -->
        <div
          class="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
          :class="scoreHeaderBg"
        >
          {{ initials }}
        </div>

        <!-- Info base -->
        <div class="flex-1 min-w-0">
          <div class="flex flex-wrap items-center gap-2 mb-1">
            <h2 class="text-xl font-bold text-gray-900 dark:text-white">
              {{ detail.name }} {{ detail.surname }}
            </h2>
            <span class="text-sm text-gray-400">#{{ detail.uid }}</span>
            <!-- Score badge -->
            <span
              class="px-2.5 py-0.5 rounded-full text-sm font-bold"
              :class="scoreBadgeClass(detail.score)"
            >
              Score {{ detail.score }}
            </span>
            <!-- Level label -->
            <span
              class="px-2 py-0.5 rounded text-xs font-medium"
              :class="levelBadgeClass(detail.level)"
            >
              {{ levelLabel(detail.level) }}
            </span>
            <!-- Bloccato -->
            <span
              v-if="detail.in_blacklist"
              class="px-2 py-0.5 bg-red-600 text-white rounded text-xs font-bold"
            >
              🚫 BLOCCATO
            </span>
          </div>
          <p class="text-sm text-gray-500">{{ detail.email }}</p>
          <p class="text-xs text-gray-400 mt-0.5">
            <span v-if="detail.profile_type">
              {{ profileEmoji(detail.profile_type) }} {{ detail.profile_type }} ·
            </span>
            Iscritto {{ relativeDate(detail.created_at) }}
          </p>
        </div>

        <!-- Action buttons -->
        <div class="flex flex-wrap gap-2 flex-shrink-0">
          <button
            v-if="!detail.in_blacklist"
            class="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition"
            @click="openModal('block')"
          >
            <span class="material-icons text-sm">block</span>
            Blocca
          </button>
          <button
            v-else
            class="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition"
            @click="openModal('unblock')"
          >
            <span class="material-icons text-sm">check_circle</span>
            Sblocca
          </button>
          <button
            class="flex items-center gap-1 px-3 py-1.5 border border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg text-sm transition"
            @click="openModal('note')"
          >
            <span class="material-icons text-sm">edit_note</span>
            Nota
          </button>
        </div>
      </div>

      <!-- ── GRIGLIA PRINCIPALE ────────────────────────────── -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">

        <!-- Score signals -->
        <div class="bg-white dark:bg-gray-900 rounded-xl shadow p-4">
          <h3 class="font-semibold text-gray-900 dark:text-white mb-3">Segnali di rischio</h3>
          <div v-if="!detail.signals || !detail.signals.length" class="text-gray-400 text-sm">
            Nessun segnale attivo.
          </div>
          <ul v-else class="space-y-2">
            <li
              v-for="sig in detail.signals"
              :key="sig.code"
              class="flex items-center justify-between gap-2"
            >
              <div class="flex items-center gap-2">
                <span
                  class="text-xs font-mono px-1.5 py-0.5 rounded"
                  :class="sig.points > 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'"
                >
                  {{ sig.code }}
                </span>
                <span class="text-sm text-gray-700 dark:text-gray-300">{{ sig.label }}</span>
              </div>
              <span
                class="text-sm font-bold tabular-nums"
                :class="sig.points > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'"
              >
                {{ sig.points > 0 ? '+' : '' }}{{ sig.points }}
              </span>
            </li>
          </ul>
        </div>

        <!-- Info + non pagato -->
        <div class="bg-white dark:bg-gray-900 rounded-xl shadow p-4 space-y-3">
          <h3 class="font-semibold text-gray-900 dark:text-white mb-3">Dati account</h3>

          <div class="flex justify-between text-sm">
            <span class="text-gray-500">Non pagato</span>
            <span
              :class="detail.unpaid_eur > 0 ? 'text-red-600 dark:text-red-400 font-bold' : 'text-gray-300'"
            >
              {{ detail.unpaid_eur > 0 ? formatEur(detail.unpaid_eur) : '€0' }}
              <span v-if="detail.unpaid_count > 1" class="text-xs font-normal ml-1">
                ({{ detail.unpaid_count }} fatture)
              </span>
            </span>
          </div>

          <div v-if="detail.in_blacklist" class="flex justify-between text-sm">
            <span class="text-gray-500">Motivo blocco</span>
            <span class="text-red-600 dark:text-red-400 text-right max-w-[60%] text-xs italic">
              "{{ detail.blacklist_reason }}"
            </span>
          </div>

          <div v-if="detail.notes" class="text-sm">
            <p class="text-gray-500 mb-1">Note operative</p>
            <p class="text-gray-700 dark:text-gray-300 text-xs italic bg-gray-50 dark:bg-gray-800 rounded p-2">
              {{ detail.notes }}
            </p>
          </div>
        </div>
      </div>

      <!-- ── CARTE ─────────────────────────────────────────── -->
      <div class="bg-white dark:bg-gray-900 rounded-xl shadow p-4 mb-4">
        <h3 class="font-semibold text-gray-900 dark:text-white mb-3">
          Carte Stripe
          <span class="text-xs text-gray-400 font-normal ml-1">({{ (detail.cards || []).length }} attive)</span>
        </h3>
        <div v-if="!detail.cards || !detail.cards.length" class="text-gray-400 text-sm">Nessuna carta.</div>
        <div v-else class="flex flex-wrap gap-3">
          <div
            v-for="card in detail.cards"
            :key="card._id"
            class="flex items-center gap-3 px-3 py-2 rounded-lg border"
            :class="card.favourite ? 'border-blue-200 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700'"
          >
            <span class="material-icons text-gray-400 text-2xl">credit_card</span>
            <div>
              <p class="text-sm font-medium text-gray-900 dark:text-white">
                {{ cardBrandLabel(card) }}
                <span v-if="card.favourite" class="ml-1 text-xs text-blue-600 dark:text-blue-400">★ Preferita</span>
                <span v-if="card.fraud_whitelist" class="ml-1 text-xs text-green-600 dark:text-green-400">✓ Legittima</span>
              </p>
              <p class="text-xs text-gray-400">
                scad. {{ String(card.exp_month || '?').padStart(2, '0') }}/{{ card.exp_year || '?' }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- ── CLUSTER: altri account stessa carta ──────────── -->
      <div
        v-if="detail.card_cluster_others && detail.card_cluster_others.length > 0"
        class="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-4"
      >
        <h3 class="font-semibold text-amber-800 dark:text-amber-300 mb-3">
          <span class="material-icons text-sm align-middle mr-1">warning</span>
          Carta condivisa con {{ detail.card_cluster_others.length }} altri account
        </h3>
        <div class="flex flex-wrap gap-2">
          <router-link
            v-for="other in detail.card_cluster_others"
            :key="other.uid"
            :to="{ name: 'menu.antifrode.utenti.detail', params: { uid: other.uid } }"
            class="flex items-center gap-2 px-2 py-1 bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-700 hover:border-amber-400 transition text-sm"
          >
            <span class="text-gray-700 dark:text-gray-300">{{ other.name }} {{ other.surname }}</span>
            <span class="text-xs text-gray-400">#{{ other.uid }}</span>
          </router-link>
        </div>
      </div>

      <!-- ── ULTIMI EVENTI (pagamenti) ─────────────────────── -->
      <div class="bg-white dark:bg-gray-900 rounded-xl shadow p-4 mb-4">
        <h3 class="font-semibold text-gray-900 dark:text-white mb-3">
          Ultimi pagamenti
          <span class="text-xs text-gray-400 font-normal ml-1">(max 30)</span>
        </h3>
        <div v-if="!detail.recent_events || !detail.recent_events.length" class="text-gray-400 text-sm">
          Nessun pagamento registrato.
        </div>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-xs text-gray-400 border-b border-gray-100 dark:border-gray-800">
              <th class="pb-1.5 text-left">Descrizione</th>
              <th class="pb-1.5 text-left hidden sm:table-cell">Stato</th>
              <th class="pb-1.5 text-right">Importo</th>
              <th class="pb-1.5 text-right hidden md:table-cell">Data</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
            <tr
              v-for="(ev, i) in detail.recent_events"
              :key="i"
              class="text-xs"
            >
              <td class="py-1.5 text-gray-700 dark:text-gray-300 pr-2 max-w-[180px] truncate">
                {{ ev.description || ev.ref_id || '—' }}
              </td>
              <td class="py-1.5 hidden sm:table-cell">
                <span
                  class="px-1.5 py-0.5 rounded text-xs"
                  :class="ev.tx_success ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'"
                >
                  {{ ev.tx_success ? 'OK' : ev.status || 'Fallito' }}
                </span>
              </td>
              <td class="py-1.5 text-right font-mono">
                {{ ev.tx_amount_cents ? formatEur(ev.tx_amount_cents / 100) : '—' }}
              </td>
              <td class="py-1.5 text-right text-gray-400 hidden md:table-cell whitespace-nowrap">
                {{ relativeDate(ev.created_at) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ── TENTATIVI SIGNUP ──────────────────────────────── -->
      <div
        v-if="detail.signup_attempts && detail.signup_attempts.length > 0"
        class="bg-white dark:bg-gray-900 rounded-xl shadow p-4 mb-4"
      >
        <h3 class="font-semibold text-gray-900 dark:text-white mb-3">
          Tentativi registrazione associati (stessa email)
        </h3>
        <table class="w-full text-xs">
          <thead>
            <tr class="text-gray-400 border-b border-gray-100 dark:border-gray-800">
              <th class="pb-1.5 text-left">IP</th>
              <th class="pb-1.5 text-left">Profilo</th>
              <th class="pb-1.5 text-left">Esito</th>
              <th class="pb-1.5 text-right">Data</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
            <tr v-for="(att, i) in detail.signup_attempts" :key="i">
              <td class="py-1 font-mono text-gray-700 dark:text-gray-300">{{ att.ip || '—' }}</td>
              <td class="py-1">{{ att.profile_type || '—' }}</td>
              <td class="py-1">
                <span
                  class="px-1.5 py-0.5 rounded"
                  :class="att.outcome === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'"
                >
                  {{ att.outcome || '—' }}
                </span>
              </td>
              <td class="py-1 text-right text-gray-400">{{ relativeDate(att.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ── STORICO AZIONI ────────────────────────────────── -->
      <div class="bg-white dark:bg-gray-900 rounded-xl shadow p-4">
        <h3 class="font-semibold text-gray-900 dark:text-white mb-3">Storico azioni</h3>
        <div
          v-if="!detail.action_history || !detail.action_history.length"
          class="text-gray-400 text-sm"
        >
          Nessuna azione registrata.
        </div>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-xs text-gray-400 border-b border-gray-100 dark:border-gray-800">
              <th class="pb-1.5 text-left">Azione</th>
              <th class="pb-1.5 text-left hidden sm:table-cell">Operatore</th>
              <th class="pb-1.5 text-left">Motivo</th>
              <th class="pb-1.5 text-right hidden md:table-cell">Data</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
            <tr v-for="(act, i) in detail.action_history" :key="i" class="text-xs">
              <td class="py-1.5 pr-2">
                <span
                  class="px-1.5 py-0.5 rounded font-semibold"
                  :class="actionBadgeClass(act.action)"
                >
                  {{ actionLabel(act.action) }}
                </span>
              </td>
              <td class="py-1.5 text-gray-500 hidden sm:table-cell">
                {{ act.actor_email || (act.actor_uid === 0 ? 'SISTEMA' : `#${act.actor_uid}`) }}
              </td>
              <td class="py-1.5 text-gray-500 max-w-[200px] truncate" :title="act.reason">
                {{ act.reason || '—' }}
              </td>
              <td class="py-1.5 text-right text-gray-400 whitespace-nowrap hidden md:table-cell">
                {{ relativeDate(act.created_at) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ── MODALI ──────────────────────────────────────────── -->
    <flow-modal v-if="modal.open" @backdrop="closeModal">
      <template #header>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ modal.title }}</h3>
      </template>
      <template #body>
        <p v-if="modal.type === 'unblock' && detail && detail.blacklist_reason" class="text-xs text-gray-400 bg-gray-50 dark:bg-gray-800 rounded p-2 mb-3 italic">
          Motivo blocco: "{{ detail.blacklist_reason }}"
        </p>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {{ modal.type === 'note' ? 'Testo nota' : 'Motivazione' }}
          <span class="text-red-500">*</span>
        </label>
        <textarea
          v-model="modal.reason"
          class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
          rows="3"
          :placeholder="modal.type === 'note' ? 'Es: Cliente contattato, verifica in corso...' : 'Es: Verifica manuale completata il 29/05'"
        ></textarea>
        <p v-if="modal.reason.length > 0 && modal.reason.length < 5" class="text-xs text-red-500 mt-1">
          Minimo 5 caratteri.
        </p>
      </template>
      <template #footer>
        <button
          class="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800 transition"
          @click="closeModal"
        >
          Annulla
        </button>
        <button
          class="px-4 py-2 text-sm rounded-lg text-white transition disabled:opacity-50"
          :class="modal.type === 'block' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'"
          :disabled="modal.reason.length < 5 || modal.loading"
          @click="confirmModal"
        >
          {{ modal.loading ? 'In corso...' : 'Conferma' }}
        </button>
      </template>
    </flow-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      modal: { open: false, type: null, title: '', reason: '', loading: false },
    };
  },
  computed: {
    ...mapGetters(['antifrode_currentDetail', 'antifrode_loading']),
    detail() {
      return this.antifrode_currentDetail;
    },
    loading() {
      return this.antifrode_loading;
    },
    uid() {
      return this.$route.params.uid;
    },
    initials() {
      if (!this.detail) return '?';
      const n = (this.detail.name || '').charAt(0);
      const s = (this.detail.surname || '').charAt(0);
      return (n + s).toUpperCase() || '??';
    },
    scoreHeaderBg() {
      const score = this.detail?.score || 0;
      if (score >= 70) return 'bg-red-600';
      if (score >= 40) return 'bg-yellow-500';
      return 'bg-green-600';
    },
  },
  mounted() {
    this.$store.dispatch('fetchAntifrodeUserDetail', this.uid);
  },
  methods: {
    openModal(type) {
      const titles = {
        block: 'Blocca utente',
        unblock: 'Sblocca utente',
        note: 'Aggiungi nota operativa',
      };
      this.modal = { open: true, type, title: titles[type], reason: '', loading: false };
    },
    closeModal() {
      this.modal = { open: false, type: null, title: '', reason: '', loading: false };
    },
    async confirmModal() {
      this.modal.loading = true;
      const { type, reason } = this.modal;
      const uid = this.uid;
      try {
        if (type === 'block') {
          await this.$store.dispatch('antifrode_blockUser', { uid, reason });
        } else if (type === 'unblock') {
          await this.$store.dispatch('antifrode_unblockUser', { uid, reason });
        } else if (type === 'note') {
          await this.$store.dispatch('antifrode_addNote', { uid, note: reason });
        }
        this.closeModal();
        this.$store.dispatch('fetchAntifrodeUserDetail', uid);
      } catch (e) {
        console.error('Azione antifrode fallita', e);
        this.modal.loading = false;
      }
    },
    // ── helpers UI ────────────────────────────────────────────────
    scoreBadgeClass(score) {
      if (score >= 70) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      if (score >= 40) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    },
    levelBadgeClass(level) {
      const map = {
        CRITICAL: 'bg-red-600 text-white',
        HIGH: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        MEDIUM: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
        LOW: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      };
      return map[level] || 'bg-gray-100 text-gray-500';
    },
    levelLabel(level) {
      return { CRITICAL: 'Critico', HIGH: 'Alto', MEDIUM: 'Medio', LOW: 'Basso' }[level] || level;
    },
    profileEmoji(type) {
      return { FOREIGN: '🌍', PRIVATE: '🧍', BUSINESS: '🏢' }[type] || '';
    },
    cardBrandLabel(card) {
      const brand = card.brand
        ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1).toLowerCase()
        : 'Carta';
      return `${brand} ****${card.last4 || '????'}`;
    },
    actionLabel(type) {
      const labels = {
        BLOCK_USER: 'Blocco', UNBLOCK_USER: 'Sblocco',
        WHITELIST_CARD: 'Whitelist carta', ADD_NOTE: 'Nota',
        AUTO_BLOCK_HIGH_UNPAID: 'Auto-blocco debito',
        AUTO_BLOCK_EMAIL_TYPO: 'Auto-blocco email',
        AUTO_BLOCK_SHARED_CARD: 'Auto-blocco carta',
        AUTO_WHITELIST_CARD: 'Auto-whitelist',
      };
      return labels[type] || (type || '').replace(/_/g, ' ');
    },
    actionBadgeClass(type) {
      if (type?.includes('BLOCK')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      if (type?.includes('WHITELIST') || type === 'UNBLOCK_USER') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      return 'bg-gray-100 text-gray-500';
    },
    formatEur(val) {
      if (!val) return '€0';
      return new Intl.NumberFormat('it-IT', {
        style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
      }).format(val);
    },
    relativeDate(dateStr) {
      if (!dateStr) return '—';
      const diffMs = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMs / 3600000);
      const days = Math.floor(diffMs / 86400000);
      if (mins < 2) return 'adesso';
      if (mins < 60) return `${mins} min fa`;
      if (hours < 24) return `${hours}h fa`;
      if (days < 30) return `${days} gg fa`;
      if (days < 365) return `${Math.floor(days / 30)} mesi fa`;
      return `${Math.floor(days / 365)} anni fa`;
    },
  },
};
</script>

<style></style>
